import { useRef, useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { useLocation } from 'react-router-dom';
import getUserId from '../../utils/getUserId';
import cleanPath from '~/widget/utils/cleanPath';
import { assertNever } from '~/widget/utils/assertNever';
import log from '~/shared/utils/log';
import getWidgetMode from '~/shared/utils/getWidgetMode';
import { usePosthog as usePostHogContext } from '~/widget/context/PosthogContext';
import useStandaloneFlags from '../useStandaloneFlags';

type TrackingFunction = (event: TrackingEvent) => void;

type TrackingEvent =
  | {
      name: 'app_event';
      slug: string;
      messageName: string;
      is_conversion: boolean;
      properties: Record<string, any>;
      featureFlags: Record<string, any>;
    }
  | {
      name: 'app_shown';
      slug: string;
    }
  | {
      name: 'wgt_app_list_item_clicked';
      slug: string;
    }
  | { name: 'wgt_app_list_loaded' }
  | { name: 'sap_app_list_loaded' }
  | { name: 'wgt_dismissed' }
  | { name: 'wgt_loaded_daily' }
  | { name: 'wgt_loaded_monthly' }
  | { name: 'wgt_loaded_monthly_total' }
  | { name: 'wgt_trigger_shown_biedwijzer' };

/**
 * Hook that provides a tracking function for PostHog events.
 * Automatically calls identify() once before the first capture event.
 *
 * @example
 * ```tsx
 * const tracking = useTracking();
 * tracking('button_clicked', { button_name: 'Submit' });
 * ```
 */
const useTracking = (): TrackingFunction => {
  const posthog = usePostHog();
  const { getAllFeatureFlags } = usePostHogContext();
  const { noTrack } = useStandaloneFlags();

  const location = useLocation();
  const hasIdentified = useRef<boolean>(false);
  const hasBeganCapture = useRef<boolean>(false);

  const tracking = useCallback<TrackingFunction>(
    (event: TrackingEvent) => {
      // `bndl_wgt_notrack` standalone links never capture anything
      if (noTrack || !posthog) return;

      const featureFlags = getAllFeatureFlags();
      const flags = Object.entries(featureFlags).reduce(
        (acc, [featureFlag, value]) => ({
          ...acc,
          [`$feature/${featureFlag}`]: value,
        }),
        {} as Record<string, any>,
      );

      try {
        const type =
          getWidgetMode(location.pathname) === 'standalone' ? 'sap' : 'wgt';
        const cleanedPath = cleanPath(location.pathname);
        const userId = getUserId();

        const generalProperties = {
          bndl_user_id: userId,
          bndl_widget_type: type,
          bndl_path: cleanedPath,
          bndl_wgt_commit_hash: process.env.COMMIT_HASH || '',
          bndl_wgt_build_date: process.env.BUILD_DATE || '',
        };

        // Call identify() once before the first capture
        if (!hasIdentified.current) {
          posthog.identify(userId);
          hasIdentified.current = true;
        }

        INTERACTION_START: if (!hasBeganCapture.current) {
          switch (event.name) {
            case 'wgt_loaded_daily':
            case 'wgt_loaded_monthly':
            case 'wgt_loaded_monthly_total':
            case 'wgt_trigger_shown_biedwijzer':
              break INTERACTION_START;
          }

          // Also check URL for deep link indicators
          const urlParams = new URLSearchParams(location.search);
          const hasDeepLinkParams =
            urlParams.has('dhParams') ||
            urlParams.has('bndlData') ||
            urlParams.has('dhuwParams');

          // Determine trigger type
          const triggerType: 'deepLink' | 'command' | 'click' | undefined =
            (() => {
              if (hasDeepLinkParams) return 'deepLink';
              return undefined;
            })();

          posthog.capture('wgt_interaction_start', {
            ...generalProperties,
            ...flags,
            bndl_trigger_type: triggerType,
            bndl_start_event: event.name,
          });
          hasBeganCapture.current = true;
        }

        switch (event.name) {
          case 'app_event': {
            const unifiedSlug = (() => {
              /**
               * We want to combine Brainbay and Matrixian for easier analytics
               */
              switch (event.slug) {
                case 'bb-wc':
                case 'm-wc':
                  return 'bb-wc';
                default:
                  return event.slug;
              }
            })();
            const prefix = `bndl_event_${unifiedSlug}_`;

            const properties = Object.entries(event.properties).reduce(
              (acc, [key, value]) => {
                if (
                  typeof value !== 'string' &&
                  typeof value !== 'number' &&
                  typeof value !== 'boolean'
                ) {
                  return acc;
                }

                return { ...acc, [`${prefix}__${key}`]: value };
              },
              {} as Record<string, string | number | boolean>,
            );

            posthog.capture(`app_event_${unifiedSlug}__${event.messageName}`, {
              ...properties,
              ...event.featureFlags,
              ...flags,
              bndl_event_type: 'app_event',
              bndl_is_conversion: event.is_conversion,
              bndl_slug: event.slug,
              ...generalProperties,
            });

            posthog.capture('app_event', {
              ...properties,
              [`${prefix}_name`]: event.messageName,
              ...event.featureFlags,
              ...flags,
              bndl_event_type: 'app_event_general',
              bndl_is_conversion: event.is_conversion,
              bndl_slug: event.slug,
              ...generalProperties,
            });
            break;
          }
          case 'wgt_trigger_shown_biedwijzer':
            posthog.capture('wgt_trigger_shown_biedwijzer', {
              ...generalProperties,
              ...flags,
            });
            break;
          case 'wgt_app_list_item_clicked':
            posthog.capture('wgt_app_list_item_clicked', {
              bndl_slug: event.slug,
              ...generalProperties,
              ...flags,
            });
            break;

          case 'app_shown':
            posthog.capture('app_shown', {
              bndl_slug: event.slug,
              ...generalProperties,
              ...flags,
            });
            break;
          case 'sap_app_list_loaded':
          case 'wgt_app_list_loaded':
          case 'wgt_dismissed':
          case 'wgt_loaded_daily':
          case 'wgt_loaded_monthly':
          case 'wgt_loaded_monthly_total':
            posthog.capture(event.name, {
              ...generalProperties,
              ...flags,
            });
            break;

          default:
            assertNever(event);
        }
      } catch (error) {
        log(`Error capturing event: ${event.name}`, error);
      }
    },
    [posthog, location.pathname, noTrack],
  );

  return tracking;
};

export default useTracking;
